import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Link, useNavigate } from 'react-router-dom';
import '@aws-amplify/ui-react/styles.css';
import { useAuth } from './AuthContext';
import { IoIosEye, IoIosEyeOff } from 'react-icons/io';
import LoadingSpinnerPage from '../pages/LoadingSpinnerPage';
import { jwtDecode } from 'jwt-decode';
// Stage-aware endpoint (src/config/api.js): npm start -> Development, builds -> Production.
import { USER_API_URL } from '../config/api';
import BrandLockup from '../components/brand/BrandLockup';
import { authSwitchLink } from './authSwitchLink';
// The look (2026-10-28): the teacher signup's (survey.css), plus a few
// page-only rules in authLook.css. The page brings its own top bar; App.js
// gives /signin no site nav.
import '../Homepage/connect/sceneTokens.css';
import '../Homepage/connect/survey/survey.css';
import './authLook.css';

// The minimal top bar: the logo home. "Create an account" sits next to the
// Log in button instead (2026-10-29), so the bar has no button of its own.
function AuthTop() {
  return (
    <header className="ts-top">
      <Link to="/" className="ts-home" aria-label="Brain Buffs home">
        <BrandLockup />
      </Link>
    </header>
  );
}

// The help links' color: green after a send worked, red after it failed.
const helpTone = (type) => (type === 'success' ? ' is-success' : type === 'error' ? ' is-error' : '');

// (hasSeenOnboarding removed with the onboarding page — Sandy 2026-08-09:
// students go straight to the dashboard; per-page tours cover first visits.)

// ---- Clever / ClassLink SSO (v2, 2026-08-16) ----
// "Sign in with ClassLink" stays (Sandy 2026-10-29: "We can keep class link";
// about 90 St. Vrain / Erie users sign in with it). "Sign in with Clever" was
// removed (no real Clever users); its start URL was
// https://50et7rsdo1.execute-api.us-east-1.amazonaws.com/v2/cleverSSO?init=true
// (git history has the button). Both returns still work: the v2 lambdas
// (claude-sso-clever / claude-sso-classlink) send a portal launch back here
// with Cognito tokens in the URL #fragment (never the query string), which
// the fragment effect below consumes, and their errors open the dialog below.
// ClassLink's portal registers exactly ONE redirect URI, on the brainbuffs.com
// domain, so the button must enter through api.brainbuffs.com (the lambda
// derives redirect_uri from the request host, and it must match the portal).
const CLASSLINK_SSO_INIT_V2 = 'https://api.brainbuffs.com/v2/classlinkSSO?init=true';

function SignIn() {
  // UI
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // New password required state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [sendingTempPassword, setSendingTempPassword] = useState(false);
  const [tempPasswordMessage, setTempPasswordMessage] = useState('');
  const [tempPasswordMessageType, setTempPasswordMessageType] = useState('');
  const [sendingVerifyCode, setSendingVerifyCode] = useState(false);
  const [verifyCodeMessage, setVerifyCodeMessage] = useState('');
  const [verifyCodeMessageType, setVerifyCodeMessageType] = useState('');

  const { isLoggedIn, user, login, logout, updateUser } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');

  const navigate = useNavigate();

  //Checking for a redirect
  const urlParams = new URLSearchParams(window.location.search);
  const redirectParam = urlParams.get('redirect');

  // Already signed in when the page opened (Sandy 2026-10-29): this page has
  // its own top bar now, so the site nav's "Go to dashboard" is gone and the
  // spinner below would never end. Go to the dashboard, by role, as a sign-in
  // here does. The saved session (AuthContext reads the same key) loads after
  // the first render, so it is read here. Not on a Clever / ClassLink return.
  const [signedInAtOpen] = useState(() => {
    try { return Boolean(localStorage.getItem('sessionToken')); } catch { return false; }
  });
  useEffect(() => {
    if (!signedInAtOpen || !isLoggedIn || !user) return;
    if (['clever_login', 'classlink_login', 'id_token', 'error'].some((k) => urlParams.has(k))) return;
    if (user.role === 'Admin') navigate('/admin-dashboard', { replace: true });
    else if (user.role === 'Teacher') navigate('/teacher-dashboard', { replace: true });
    else navigate(redirectParam || '/student-dashboard', { replace: true });
  }, [signedInAtOpen, isLoggedIn, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  // Handle SSO error redirect from either clever-sso-handler or classlink-sso-handler
  const [showCleverError, setShowCleverError] = useState(false);
  const [cleverErrorMessage, setCleverErrorMessage] = useState('');
  const [cleverErrorType, setCleverErrorType] = useState('');
  const [ssoProvider, setSsoProvider] = useState('Clever');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    const ssoMessage = params.get('message');
    const isClasslink = params.get('classlink_login') === 'true' || (ssoError && params.get('classlink_error') === 'true');

    if (ssoError) {
      const provider = isClasslink ? 'ClassLink' : 'Clever';
      setSsoProvider(provider);
      setCleverErrorType(ssoError);
      setCleverErrorMessage(ssoMessage || `Something went wrong with ${provider} login. Please try again or contact us.`);
      setShowCleverError(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);


  useEffect(() => {
    const handleCleverSSO = async () => {
      const params = new URLSearchParams(window.location.search);
      const cleverLogin = params.get('clever_login');
      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (cleverLogin && idToken && accessToken && refreshToken) {
        try {
          setIsSigningIn(true);

          // Decode id_token to get user attributes
          const decoded = jwtDecode(idToken);

          await login({
            email: decoded.email,
            username: decoded.sub || decoded['cognito:username'],
            Name: decoded.name,
            role: decoded['custom:Role'],
            fullName: decoded.name,
            idToken,
            accessToken,
            refreshToken
          });

          const role = decoded['custom:Role'];

          // If the SSO came from a deep-link (e.g. /assignment/<id> click in
          // Classroom), the lambda propagated `redirect` through the OAuth
          // state round-trip. Honor it — but only relative paths, since the
          // server already validated. Defense-in-depth: re-check here.
          const redirectTarget = params.get('redirect');
          if (redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//') && !redirectTarget.startsWith('/\\')) {
            navigate(redirectTarget);
          } else if (role === 'Admin') {
            navigate('/admin-dashboard');
          } else if (role === 'Teacher') {
            navigate('/teacher-dashboard');
          } else {
            navigate('/student-dashboard');
          }
        } catch (error) {
          console.error('Clever SSO login failed:', error);
          setErrorMessage('Clever login failed. Please try again.');
        } finally {
          setIsSigningIn(false);
        }
      }
    };

    handleCleverSSO();
  }, []);

  useEffect(() => {
    const handleClassLinkSSO = async () => {
      const params = new URLSearchParams(window.location.search);
      const classlinkLogin = params.get('classlink_login');
      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (classlinkLogin && idToken && accessToken && refreshToken) {
        try {
          setIsSigningIn(true);

          // Decode id_token to get user attributes
          const decoded = jwtDecode(idToken);

          await login({
            email: decoded.email,
            username: decoded.sub || decoded['cognito:username'],
            Name: decoded.name,
            role: decoded['custom:Role'],
            fullName: decoded.name,
            idToken,
            accessToken,
            refreshToken
          });

          const role = decoded['custom:Role'];

          // ClassLink SSO can also propagate a deep-link redirect through OAuth
          // state — honor it if it's relative.
          const redirectTarget = params.get('redirect');
          if (redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//') && !redirectTarget.startsWith('/\\')) {
            navigate(redirectTarget);
          } else if (role === 'Admin') {
            navigate('/admin-dashboard');
          } else if (role === 'Teacher') {
            navigate('/teacher-dashboard');
          } else {
            navigate('/student-dashboard');
          }
        } catch (error) {
          console.error('ClassLink SSO login failed:', error);
          setErrorMessage('ClassLink login failed. Please try again.');
        } finally {
          setIsSigningIn(false);
        }
      }
    };

    handleClassLinkSSO();
  }, []);

  // v2 SSO callback: the claude-sso-* lambdas return tokens in the URL
  // FRAGMENT (#clever_login=... / #classlink_login=...) so they never hit
  // server logs. The v1 query-param effects above stay for back-compat.
  useEffect(() => {
    const handleFragmentSSO = async () => {
      const hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      const params = new URLSearchParams(hash.slice(1));
      const isClever = params.get('clever_login') === 'true';
      const isClassLink = params.get('classlink_login') === 'true';
      if (!isClever && !isClassLink) return;

      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const provider = isClassLink ? 'ClassLink' : 'Clever';

      // Scrub the tokens out of the address bar / history immediately.
      window.history.replaceState({}, '', window.location.pathname + window.location.search);

      if (!idToken || !accessToken || !refreshToken) {
        setErrorMessage(`${provider} login failed. Please try again.`);
        return;
      }

      try {
        setIsSigningIn(true);
        const decoded = jwtDecode(idToken);

        await login({
          email: decoded.email,
          username: decoded.sub || decoded['cognito:username'],
          Name: decoded.name,
          role: decoded['custom:Role'],
          fullName: decoded.name,
          idToken,
          accessToken,
          refreshToken
        });

        const role = decoded['custom:Role'];

        // Deep-link redirect tunneled through OAuth state — relative paths
        // only (server validated already; re-check as defense-in-depth).
        const redirectTarget = params.get('redirect');
        if (redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//') && !redirectTarget.startsWith('/\\')) {
          navigate(redirectTarget);
        } else if (role === 'Admin') {
          navigate('/admin-dashboard');
        } else if (role === 'Teacher') {
          navigate('/teacher-dashboard');
        } else {
          navigate('/student-dashboard');
        }
      } catch (error) {
        console.error(`${provider} SSO login failed:`, error);
        setErrorMessage(`${provider} login failed. Please try again.`);
      } finally {
        setIsSigningIn(false);
      }
    };

    handleFragmentSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value.trim());
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  async function handleCompleteNewPassword(event) {
    event.preventDefault();

    if (isSigningIn) return;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }

    setIsSigningIn(true);
    setErrorMessage('');

    try {
      // Complete the new password challenge
      await Auth.completeNewPassword(cognitoUser, newPassword);

      // After completing password, get the fully authenticated user
      const userInfo = await Auth.currentAuthenticatedUser();

      // Validate userInfo and its properties
      if (!userInfo || !userInfo.signInUserSession) {
        throw new Error('Invalid authentication response after password change');
      }

      // Validate user attributes
      if (!userInfo.attributes) {
        throw new Error('User attributes not available. Please try again.');
      }

      const idToken = userInfo.signInUserSession.idToken.jwtToken;
      const accessToken = userInfo.signInUserSession.accessToken.jwtToken;
      const refreshToken = userInfo.signInUserSession.refreshToken.token;

      // Login with validated user information
      await login({
        email: userInfo.attributes.email,
        username: userInfo.attributes.sub,
        Name: userInfo.attributes.name,
        phone_Number: userInfo.attributes.phone_number,
        fullName: userInfo.attributes['custom:FullName'],
        role: userInfo.attributes['custom:Role'],
        phoneNumber: userInfo.attributes['custom:PhoneNumber'],
        idToken,
        accessToken,
        refreshToken
      });

      // New-stack account record self-heal (ClaudeUsers via claude-user-api):
      // idempotent, fills gaps only - covers accounts whose signup flow was
      // interrupted before onboarding ran. Fire-and-forget, never blocks login.
      try {
        fetch(USER_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: idToken },
          body: JSON.stringify({
            action: 'onboardUser',
            school: userInfo.attributes['custom:School'] || null,
            name: userInfo.attributes.name || null,
            source: 'signin-selfheal',
          }),
        }).catch(() => {});
      } catch (e) { /* never block login on profile population */ }

      const role = userInfo.attributes['custom:Role'];
      const currentDate = new Date().toISOString();

      // Handle navigation based on URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const plan = urlParams.get('plan');
      const source = urlParams.get('source');

      if (plan === 'pro' || plan === 'practice') {
        const planType = plan === 'pro' ? 'pro' : 'practice';
        navigate(`/checkout?plan=${planType}&email=${username}`);
      } else if (plan === 'premium') {
        window.location.href = `https://buy.stripe.com/6oE17ubvhcsIczS9AE?prefilled_email=${username}`;
      } else if (role === 'Admin') {
        navigate('/admin-dashboard');
      } else if (role === 'Teacher') {
        navigate('/teacher-dashboard');
      } else {
        // Only students go through onboarding and student-specific logic
        const lastLogin = userInfo.attributes['custom:LastLogin'];

        try {
          await Auth.updateUserAttributes(userInfo, {
            'custom:LastLogin': currentDate
          });
        } catch (updateError) {
          console.error('Error updating last login:', updateError);
        }

        if (source === 'buff') {
          navigate('/diagnostic-mini-intro');
        } else {
          // Onboarding page removed (Sandy 2026-08-09): every student lands
          // straight on the dashboard, which carries its own tour.
          navigate(redirectParam || '/student-dashboard');
        }
      }

    } catch (error) {
      console.error('Error completing new password:', error);
      setErrorMessage(error.message || 'Failed to set new password. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  }

  async function handleSignIn(event) {
    event.preventDefault();
    if (isSigningIn) return;
    setIsSigningIn(true);
    setErrorMessage('');

    if (event.key === 'Enter') {
      event.preventDefault();
    }

    try {
      const trimmedUsername = username.trim();

      // Add validation
      if (!trimmedUsername) {
        setErrorMessage('Email is required');
        setIsSigningIn(false);
        return;
      }

      if (!password) {
        setErrorMessage('Password is required');
        setIsSigningIn(false);
        return;
      }

      // Sign in with AWS Cognito
      const userInfo = await Auth.signIn(trimmedUsername, password);

      // Check if new password is required
      if (userInfo.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('New password required for user');
        setCognitoUser(userInfo);
        setNeedsNewPassword(true);
        setIsSigningIn(false);
        return;
      }

      // Validate userInfo and its properties
      if (!userInfo || !userInfo.signInUserSession) {
        throw new Error('Invalid authentication response');
      }

      // Add a small delay to ensure attributes are populated
      await new Promise(resolve => setTimeout(resolve, 500));

      // Validate user attributes
      if (!userInfo.attributes) {
        throw new Error('User attributes not available. Please try again.');
      }

      const idToken = userInfo.signInUserSession.idToken.jwtToken;
      const accessToken = userInfo.signInUserSession.accessToken.jwtToken;
      const refreshToken = userInfo.signInUserSession.refreshToken.token;

      // Login with validated user information
      await login({
        email: userInfo.attributes.email,
        username: userInfo.attributes.sub,
        Name: userInfo.attributes.name,
        phone_Number: userInfo.attributes.phone_number,
        fullName: userInfo.attributes['custom:FullName'],
        role: userInfo.attributes['custom:Role'],
        phoneNumber: userInfo.attributes['custom:PhoneNumber'],
        idToken,
        accessToken,
        refreshToken
      });

      // New-stack account record self-heal (ClaudeUsers via claude-user-api):
      // idempotent, fills gaps only - covers accounts whose signup flow was
      // interrupted before onboarding ran. Fire-and-forget, never blocks login.
      try {
        fetch(USER_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: idToken },
          body: JSON.stringify({
            action: 'onboardUser',
            school: userInfo.attributes['custom:School'] || null,
            name: userInfo.attributes.name || null,
            source: 'signin-selfheal',
          }),
        }).catch(() => {});
      } catch (e) { /* never block login on profile population */ }

      const role = userInfo.attributes['custom:Role'];
      const currentDate = new Date().toISOString();

      // Handle navigation based on URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const plan = urlParams.get('plan');
      const source = urlParams.get('source');

      if (plan === 'pro' || plan === 'practice') {
        const planType = plan === 'pro' ? 'pro' : 'practice';
        navigate(`/checkout?plan=${planType}&email=${trimmedUsername}`);
      } else if (plan === 'premium') {
        window.location.href = `https://buy.stripe.com/6oE17ubvhcsIczS9AE?prefilled_email=${trimmedUsername}`;
      } else if (role === 'Admin') {
        navigate('/admin-dashboard');
      } else if (role === 'Teacher') {
        navigate('/teacher-dashboard');
      } else {
        // Only students go through onboarding and student-specific logic
        const lastLogin = userInfo.attributes['custom:LastLogin'];

        try {
          await Auth.updateUserAttributes(userInfo, {
            'custom:LastLogin': currentDate
          });
        } catch (updateError) {
          console.error('Error updating last login:', updateError);
          // Continue with navigation even if update fails
        }

        if (source === 'buff') {
          navigate('/diagnostic-mini-intro');
        } else {
          // Onboarding page removed (Sandy 2026-08-09): straight to the
          // dashboard; the per-page tour handles first-visit guidance.
          navigate(redirectParam || '/student-dashboard');
        }
      }

    } catch (error) {
      console.error('Sign-in error:', error);

      // Handle specific error cases
      if (error.code === 'UserNotConfirmedException') {
        // Account exists but the email was never verified: resend a code
        // and send the user to the verification page.
        try {
          await Auth.resendSignUp(username.trim());
        } catch (resendError) {
          console.error('Error resending verification code:', resendError);
        }
        navigate('/verificationCode');
        return;
      } else if (error.code === 'UserNotFoundException') {
        // Email is not registered. Cognito only returns this code when the
        // app client's "Prevent user existence errors" setting is Legacy;
        // while it is enabled, unknown emails come back as
        // NotAuthorizedException and fall into the branch below.
        setErrorMessage('Email not found. Check the address or sign up for an account.');
      } else if (error.code === 'NotAuthorizedException') {
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('attempts exceeded')) {
          setErrorMessage('Too many failed attempts. Please wait a few minutes and try again.');
        } else if (msg.includes('disabled')) {
          setErrorMessage('This account has been disabled. Please contact support.');
        } else {
          setErrorMessage('Incorrect password. Please try again or reset your password.');
        }
      } else if (error.code === 'PasswordResetRequiredException') {
        setErrorMessage('A password reset is required for this account. Use "Forgot password" to set a new one.');
      } else if (error.message.includes('Value at \'userAlias\' failed to satisfy constraint') ||
        error.message.includes('Value at \'userName\' failed to satisfy constraint')) {
        setErrorMessage('Incorrect username. There may be an unexpected space in the email field.');
      } else if (error.message.includes('User attributes not available')) {
        setErrorMessage('Authentication error. Please try again.');
      } else {
        setErrorMessage(error.message);
      }
    } finally {
      setIsSigningIn(false);
    }
  }

  const handleResendTempPassword = async () => {
    if (!username || username.trim() === '') {
      setTempPasswordMessage('Enter your email address in the field first');
      setTempPasswordMessageType('error');
      setPassword('');

      // Clear message after 15 seconds
      setTimeout(() => {
        setTempPasswordMessage('');
        setTempPasswordMessageType('');
      }, 8000);
      return;
    }

    setSendingTempPassword(true);
    setPassword('');
    setTempPasswordMessage('');
    setTempPasswordMessageType('');

    try {
      const response = await fetch('https://cvb1c4mkl8.execute-api.us-east-1.amazonaws.com/getNewTempPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send temporary password');
      }

      // Success message
      setTempPasswordMessage('✓ Password sent! Check your email');
      setTempPasswordMessageType('success');

      // Clear message after 15 seconds
      setTimeout(() => {
        setTempPasswordMessage('');
        setTempPasswordMessageType('');
      }, 8000);

    } catch (error) {
      console.error('Error sending temporary password:', error);

      // Error message
      setTempPasswordMessage(error.message || 'Failed to send password');
      setTempPasswordMessageType('error');

      // Clear message after 15 seconds
      setTimeout(() => {
        setTempPasswordMessage('');
        setTempPasswordMessageType('');
      }, 5000);
    } finally {
      setSendingTempPassword(false);
    }
  };

  const handleGetVerificationCode = async () => {
    if (!username || username.trim() === '') {
      setVerifyCodeMessage('Enter your email address in the field first');
      setVerifyCodeMessageType('error');

      setTimeout(() => {
        setVerifyCodeMessage('');
        setVerifyCodeMessageType('');
      }, 8000);
      return;
    }

    setSendingVerifyCode(true);
    setVerifyCodeMessage('');
    setVerifyCodeMessageType('');

    try {
      await Auth.resendSignUp(username.trim());

      setVerifyCodeMessage('✓ Code sent! Taking you to the verification page...');
      setVerifyCodeMessageType('success');

      setTimeout(() => {
        navigate('/verificationCode');
      }, 1500);
    } catch (error) {
      console.error('Error sending verification code:', error);

      if (error.message && error.message.toLowerCase().includes('already confirmed')) {
        setVerifyCodeMessage('Your email is already verified. Try signing in, or reset your password.');
      } else if (error.code === 'UserNotFoundException') {
        setVerifyCodeMessage('No account found with this email. Please sign up first.');
      } else {
        setVerifyCodeMessage(error.message || 'Failed to send verification code');
      }
      setVerifyCodeMessageType('error');

      setTimeout(() => {
        setVerifyCodeMessage('');
        setVerifyCodeMessageType('');
      }, 8000);
    } finally {
      setSendingVerifyCode(false);
    }
  };

  const handleSignOut = async () => {
    try {
      logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Render new password form if needed
  if (needsNewPassword) {
    return (
      <div className="cx-root marketing-type ts-page al-page">
        <AuthTop />
        <main className="ts-main">
          <div className="ts-form">
            <div key="new-password" className="ts-step">
              <h1 className="ts-title">Set new password</h1>
              <p className="ts-sub">
                You're signing in for the first time. Please set a new password to continue.
              </p>
              <form onSubmit={handleCompleteNewPassword}>
                <div className="ts-fieldset ts-form-fields">
                  <div className="ts-field">
                    <label className="ts-field-label" htmlFor="al-new-password">New password</label>
                    <div className="al-pw">
                      <input
                        id="al-new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        aria-describedby="al-new-password-hint"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="al-eye"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <IoIosEyeOff /> : <IoIosEye />}
                      </button>
                    </div>
                    <span className="ts-hint" id="al-new-password-hint">At least 8 characters</span>
                  </div>
                  <div className="ts-field">
                    <label className="ts-field-label" htmlFor="al-confirm-password">Confirm new password</label>
                    <div className="al-pw">
                      <input
                        id="al-confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="al-eye"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <IoIosEyeOff /> : <IoIosEye />}
                      </button>
                    </div>
                  </div>
                </div>
                {errorMessage && <p className="ts-error" role="alert">{errorMessage}</p>}
                <div className="ts-actions">
                  <button type="submit" className="ts-btn" disabled={isSigningIn}>
                    {isSigningIn ? 'Setting password…' : 'Set password and continue'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Its own root (fixed over the page), so it keeps the look in either state.
  const CleverErrorOverlay = () => (
    <div className="cx-root marketing-type al-page al-overlay" onClick={(e) => e.target === e.currentTarget && setShowCleverError(false)}>
      <div className="al-dialog" role="alertdialog" aria-labelledby="al-sso-title">
        <h2 className="al-dialog-title" id="al-sso-title">{ssoProvider} login issue</h2>
        <p className="al-dialog-text">{cleverErrorMessage}</p>
        <p className="al-dialog-code">Error: {cleverErrorType}</p>
        <p className="al-dialog-text is-muted">If this keeps happening, please let us know so we can help.</p>
        <div className="al-dialog-actions">
          <button type="button" onClick={() => setShowCleverError(false)} className="ts-btn ts-btn--line">
            Close
          </button>
          <a href="/contact" className="ts-btn">
            Contact us
          </a>
        </div>
      </div>
    </div>
  );

  if (isLoggedIn) {
    // Signed in: the loading screen while the redirect runs (unchanged).
    return (
      <div className="bg-white font-sans">
        {showCleverError && <CleverErrorOverlay />}
        <div>
          <LoadingSpinnerPage />
        </div>
      </div>
    );
  }

  return (
    <div className="cx-root marketing-type ts-page al-page">
      {showCleverError && <CleverErrorOverlay />}
      <AuthTop />
      <main className="ts-main">
        <div className="ts-form">
          <div key="sign-in" className="ts-step">
            <h1 className="ts-title">Log in</h1>
            <p className="ts-sub">Welcome back to Brain Buffs.</p>
            <form onSubmit={handleSignIn}>
              <div className="ts-fieldset ts-form-fields">
                <div className="ts-field">
                  <label className="ts-field-label" htmlFor="al-email">Email</label>
                  <input
                    id="al-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={username}
                    onChange={handleUsernameChange}
                  />
                </div>
                <div className="ts-field">
                  <label className="ts-field-label" htmlFor="al-password">Password</label>
                  <div className="al-pw">
                    <input
                      id="al-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={handlePasswordChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="al-eye"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <IoIosEyeOff /> : <IoIosEye />}
                    </button>
                  </div>
                </div>
              </div>
              {errorMessage && <p className="ts-error" role="alert">{errorMessage}</p>}
              {/* Log in, and the way to the other page beside it (Sandy
                  2026-10-29), carrying redirect / plan / source over. */}
              <div className="ts-actions al-pair">
                <button type="submit" className="ts-btn" disabled={isSigningIn}>
                  {isSigningIn ? 'Logging in…' : 'Log in'}
                </button>
                <Link to={authSwitchLink('/signup', window.location.search)} className="ts-btn ts-btn--line">
                  Create an account
                </Link>
              </div>
            </form>
            {/* ClassLink (back 2026-10-29): "or", then one outline button
                across the column. Outside the form, so it never submits it. */}
            <div className="al-or">or</div>
            <div className="al-sso">
              <button
                type="button"
                onClick={() => {
                  // Hand off to claude-sso-classlink in init mode: it builds
                  // the LaunchPad OAuth URL and 302s the user to ClassLink.
                  const url = redirectParam
                    ? `${CLASSLINK_SSO_INIT_V2}&redirect=${encodeURIComponent(redirectParam)}`
                    : CLASSLINK_SSO_INIT_V2;
                  window.location.href = url;
                }}
                disabled={isSigningIn}
                className="ts-btn ts-btn--line"
              >
                Sign in with ClassLink
              </button>
            </div>
            {/* Help, open at the start as before. The buttons sit outside the
                sign-in form, so none of them submits it. */}
            <div className="al-help">
              <button type="button" className="al-help-toggle" onClick={toggleExpand} aria-expanded={expanded}>
                Issues logging in?
              </button>
              {expanded && (
                <ul className="al-help-list">
                  <li>
                    <button
                      type="button"
                      onClick={handleResendTempPassword}
                      disabled={sendingTempPassword || tempPasswordMessage !== ''}
                      className={`ts-more${helpTone(tempPasswordMessageType)}`}
                    >
                      {sendingTempPassword
                        ? 'Sending...'
                        : tempPasswordMessage
                          ? tempPasswordMessage
                          : 'Reset password'}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={handleResendTempPassword}
                      disabled={sendingTempPassword || tempPasswordMessage !== ''}
                      className={`ts-more${helpTone(tempPasswordMessageType)}`}
                    >
                      {sendingTempPassword
                        ? 'Sending...'
                        : tempPasswordMessage
                          ? tempPasswordMessage
                          : 'Get a new temporary password'}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={handleGetVerificationCode}
                      disabled={sendingVerifyCode || verifyCodeMessage !== ''}
                      className={`ts-more${helpTone(verifyCodeMessageType)}`}
                    >
                      {sendingVerifyCode
                        ? 'Sending...'
                        : verifyCodeMessage
                          ? verifyCodeMessage
                          : 'Get a verification code'}
                    </button>
                  </li>
                  <li>
                    <a href="./contact" className="ts-more">
                      Contact us
                    </a>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SignIn;